import './center-view.component.ajscss';

import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { ChannelDeprecatedImpl as ChannelUtil } from '@commons/models/channel';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions } from 'angular';
import { filter, from, map, mergeMap, Observable } from 'rxjs';

import { ChanEnum, ChannelDirection, ChannelType, IMetricService, IMetricValue, MetricId, Option, toChannelId } from '@contact/upgrade';

import templateUrl from './center-view.component.ajs.html';
import { ChannelConfig } from './models/channel-config';

const MODULE_NAME = 'contactCenterView';

class CenterViewCtrl implements IComponentController {
  public name = MODULE_NAME;

  channels: string[] = [];
  channelMetrics: { [channel: string]: ChannelConfig } = {};

  private agentsMetricsMap: { [oldMetric: string]: string } = {
    SUM_CSR_PRESENT_SKILL_NOW: 'skill_nb_agent_present_now',
    SUM_CSR_NOT_WORKING_SKILL_NOW: 'skill_nb_agent_break_now',
    SUM_CSR_TASK_SKILL_NOW: 'skill_nb_agent_task_now',
    SUM_CSR_AVAILABLE_channel_SKILL_NOW: 'skill_nb_agent_available_channel_now',
    SUM_CSR_WORKING_channel_SKILL_NOW: 'skill_nb_agent_working_channel_now',
  };

  private currentContactsMetricsMap: { [oldMetric: string]: string } = {
    SUM_CTT_CREATED_channel_SKILL_NOW: 'skill_nb_contact_waiting_channel_now',
    SUM_CTT_PROCESSING_channel_SKILL_NOW: 'skill_nb_contact_processing_channel_now',
  };

  private endedContactsMetricsMap: { [oldMetric: string]: string } = {
    SUM_CTT_CREATED_channel_SKILL: 'skill_nb_contact_presented_channel',
    SUM_CTT_SUCCESS_channel_SKILL: 'skill_nb_contact_success_channel',
    SUM_CTT_NOT_PROCESSED_channel_SKILL: 'skill_nb_contact_not_processed_channel',
    SUM_CTT_GAVE_UP_SKILL: 'skill_nb_contact_gave_up_channel',
    SUM_CTT_TRANSFERRED_channel_SKILL: 'skill_nb_forward_to_agent_channel',
    SUM_CTT_REDIRECTED_channel_SKILL: 'skill_nb_forward_to_skills_channel',
    SUM_SCRIPT_ANCHOR_REDIRECTION_SKILL: 'skill_nb_forward_to_script_anchor_channel',
  };

  private waitingTimeMetricMap: { [oldMetric: string]: string } = { TA_CTT_WAITING_TIME_SKILL: 'skill_ta_contact_waiting_channel' };

  private processingRateMetricMap: { [oldMetric: string]: string } = { RATE_CTT_PROCESSED_SKILL: 'skill_rate_contact_processed_channel' };

  private metricAlignmentRight: { [oldLabel: string]: boolean } = {};

  constructor(
    private readonly metricService: IMetricService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService
  ) {}

  private toOldMetricList(metricsMap: { [oldMetric: string]: string }): string[] {
    let result: string[] = [];
    for (let metric in metricsMap) {
      result.push(metric);
    }
    return result;
  }

  $onInit() {
    let resetHour = 0;
    let taFormat = 'mm[m]ss[s]';
    let toMetricId = (metricName: string, entityId: string, channelStr: string, documentId: string): Option<MetricId> =>
      ChannelUtil.from(channelStr).map(channel => {
        let metricId: MetricId = MetricId.of(metricName).withId(entityId).withDocumentId(documentId);
        if (metricName.indexOf('channel') > 0) {
          metricId = metricId.addLiteralParam(ChannelType[channel.type]).addLiteralParam(ChannelDirection[channel.direction]);
        }
        if (metricName.indexOf('now') <= 0) {
          metricId = metricId.since(resetHour);
        }
        if (metricName.indexOf('_ta_') > 0) {
          metricId.withTimeFormat(taFormat);
        }
        if (metricName.indexOf('_rate_') > 0) {
          metricId.withRateFormat();
        }
        return metricId;
      });

    this.channels = Object.keys(this.agentConfigDeprecatedService.metricChannels);
    from(this.channels)
      .pipe(
        // if agent has visible skills
        filter(
          (channel: string) =>
            this.agentConfigDeprecatedService
              .channelSkills(channel)
              .filter(({ skill }) => this.agentConfigDeprecatedService.visibleSkillIds.indexOf(skill.id) >= 0).length > 0
        ),
        // transform to channel conf
        map((channel: string) => {
          let metrics = this.agentConfigDeprecatedService.metricChannels[channel] || [];
          let replaceFunc = (label: string) => label.replace('channel', channel);
          let containsFunc = (metricLabel: string) => metrics.indexOf(metricLabel) >= 0;
          let metricIds: MetricId[] = [];
          let skills = this.agentConfigDeprecatedService
            .channelSkills(channel)
            .filter(({ skill }) => this.agentConfigDeprecatedService.visibleSkillIds.indexOf(skill.id) >= 0)
            .sort(({ skill: skillA }, { skill: skillB }) =>
              skillA.name.localeCompare(skillB.name, undefined, { sensitivity: 'base', numeric: true })
            )
            .map(({ skill }) => skill);

          let toMetricLabels = (metricsMap: { [oldMetric: string]: string }): string[] =>
            this.toOldMetricList(metricsMap)
              .map<[string, Observable<MetricId>]>(metric => {
                let oldLabel = replaceFunc(metric);
                let ids = from(skills.map(skill => skill.id)).pipe(
                  mergeMap(skillId => {
                    if ('SUM_CTT_CREATED_channel_SKILL_NOW' === metric && channel === toChannelId(ChanEnum.In.Mail)) {
                      return Option.of(
                        MetricId.of('skill_value_metric')
                          .withId(skillId)
                          .addLiteralParam('SUM_CONTACT_WAITING_EMAILS_BY_SKILL')
                          .withDocumentId(this.metricKey(skillId, oldLabel, channel))
                      ).toObservable();
                    }
                    if ('SUM_CTT_PROCESSING_channel_SKILL_NOW' === metric && channel === toChannelId(ChanEnum.In.Mail)) {
                      return Option.of(
                        MetricId.of('skill_nb_contact_processing_mail_incoming_now')
                          .withId(skillId)
                          .withDocumentId(this.metricKey(skillId, oldLabel, channel))
                      ).toObservable();
                    }
                    if ('SUM_CTT_CREATED_channel_SKILL' === metric && channel === toChannelId(ChanEnum.In.Mail)) {
                      return Option.of(
                        MetricId.of('skill_nb_contact_presented_mail_incoming')
                          .withId(skillId)
                          .since(resetHour)
                          .withDocumentId(this.metricKey(skillId, oldLabel, channel))
                      ).toObservable();
                    }
                    if ('SUM_CTT_SUCCESS_channel_SKILL' === metric && channel === toChannelId(ChanEnum.In.Mail)) {
                      return Option.of(
                        MetricId.of('skill_nb_contact_success_mail_incoming')
                          .withId(skillId)
                          .since(resetHour)
                          .withDocumentId(this.metricKey(skillId, oldLabel, channel))
                      ).toObservable();
                    }
                    if ('SUM_CSR_WORKING_channel_SKILL_NOW' === metric && channel === toChannelId(ChanEnum.In.Mail)) {
                      return Option.of(
                        MetricId.of('skill_nb_agent_working_mail_incoming_now')
                          .withId(skillId)
                          .withDocumentId(this.metricKey(skillId, oldLabel, channel))
                      ).toObservable();
                    }
                    return toMetricId(metricsMap[metric], skillId, channel, this.metricKey(skillId, oldLabel, channel)).toObservable();
                  })
                );

                return [oldLabel, ids];
              })
              .filter(next => containsFunc(next[0]))
              .map(next => {
                next[1].forEach(id => {
                  this.metricAlignmentRight[next[0]] = id.alignRight;
                  metricIds.push(id);
                });
                return next[0];
              });

          let config: ChannelConfig = {
            agentsMetrics: toMetricLabels(this.agentsMetricsMap),
            contactsMetrics: toMetricLabels(this.currentContactsMetricsMap),
            endedContactsMetrics: toMetricLabels(this.endedContactsMetricsMap),
            waitingTimeMetric: toMetricLabels(this.waitingTimeMetricMap),
            processingRateMetric: toMetricLabels(this.processingRateMetricMap),
            skills: skills,
          };
          this.channelMetrics[channel] = config;
          return metricIds;
        }),

        // subscribe to metric
        mergeMap((metricIds: MetricId[]) => this.metricService.request(metricIds)),
        ng1Destroy(this)
      )
      .subscribe((metric: IMetricValue) => {
        let matchingElements = document.getElementsByClassName(metric.documentId);
        for (let i = 0; i < matchingElements.length; i++) {
          const container = matchingElements.item(i);
          if (container) {
            container.textContent = metric.formattedValue;
          }
        }
      });
  }

  metricI18nKey(channel: string, metricLabel: string): string {
    return metricLabel.replace(channel, 'channel');
  }

  metricKey(skillId: string, metricLabel: string, channel: string): string {
    return skillId + '@' + metricLabel + '_FOR_' + channel;
  }

  isNumber(metricLabel: string): boolean {
    return this.metricAlignmentRight[metricLabel];
  }
}

export class CenterView implements IComponentOptions {
  templateUrl = templateUrl;
  controller = ['metric', 'agentConfigDeprecatedService', CenterViewCtrl];
}

export default MODULE_NAME;
